import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AiLeadQualificationResponse,
  AiQualificationCountsResponse,
  AiQualificationEmailRuleResponse,
  CreateAiQualificationEmailRuleInput,
  ListAiLeadQualificationsQuery,
  ListAiQualificationEmailRulesQuery,
  ReviewAiLeadQualificationInput,
} from '@orion/contracts';
import { api, getPage } from '../../lib/api-client.js';

function toQueryString(query: Partial<ListAiLeadQualificationsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useAiLeadQualificationsQuery(query: Partial<ListAiLeadQualificationsQuery>, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['ai-lead-qualifications', query],
    queryFn: () => getPage<AiLeadQualificationResponse[]>(`/ai/lead-qualifications${toQueryString(query)}`),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
}

export function useAiQualificationCountsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['ai-lead-qualifications', 'counts'],
    queryFn: () => api.get<AiQualificationCountsResponse>('/ai/lead-qualifications/counts'),
    enabled: options?.enabled ?? true,
    refetchInterval: 60_000,
  });
}

export function useAiLeadQualificationQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['ai-lead-qualifications', id],
    queryFn: () => api.get<AiLeadQualificationResponse>(`/ai/lead-qualifications/${id}`),
    enabled: Boolean(id),
  });
}

export function useReviewAiLeadQualification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReviewAiLeadQualificationInput }) =>
      api.post<AiLeadQualificationResponse>(`/ai/lead-qualifications/${id}/review`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-lead-qualifications'] }),
  });
}

/** Permanent delete — the "Junk" tab's other action besides moving (which
 * reuses `useReviewAiLeadQualification` above, now that re-review is
 * allowed). */
export function useDeleteAiLeadQualification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/ai/lead-qualifications/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-lead-qualifications'] }),
  });
}

function toRulesQueryString(query: Partial<ListAiQualificationEmailRulesQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useAiQualificationEmailRulesQuery(query: Partial<ListAiQualificationEmailRulesQuery> = {}) {
  return useQuery({
    queryKey: ['ai-qualification-email-rules', query],
    queryFn: () => api.get<AiQualificationEmailRuleResponse[]>(`/ai/email-rules${toRulesQueryString(query)}`),
  });
}

export function useCreateAiQualificationEmailRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAiQualificationEmailRuleInput) => api.post<AiQualificationEmailRuleResponse>('/ai/email-rules', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-qualification-email-rules'] }),
  });
}

export function useDeleteAiQualificationEmailRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/ai/email-rules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-qualification-email-rules'] }),
  });
}
